import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router';
import { apiGet, apiSend, fmtDate } from '../api';
// 2026-10-19 — a failure because the sign-in ended is not this screen's to show.
import { alertFailure, isSessionEnded } from '../utils/sessionEnd';
import { distance } from '@insitebg/plan-markup-core'; // shared measurement engine — a published package since 2026-08-12 (was a vendored copy at src/pdf-core)
// v0.x — native shell: a _blank anchor has no tab UI in the store wrapper;
// interceptExternalClick routes it to the system browser there and is a
// no-op on the web.
import { interceptExternalClick } from '../utils/nativeShell';
import {
  drainOutbox, listOutbox, subscribeOutbox, updateOutboxItem,
} from '../utils/outbox.js'; // v0.64 durable background photo upload; v0.171.0 site mode
import { ref as outboxRef } from '../utils/outbox-refs.js'; // 2026-08-20 photos queued behind an offline defect
// v0.171.0 — SITE MODE. A pin dropped on site lives in the outbox until it is
// synced; these read it back onto the plan, with its photos, so the work is
// visible before it has been uploaded (see outbox-local.js).
import {
  applyPendingToDefects, reconcilePending, isLocalId, rowIdFromLocalId, localIdFor, mergeQueuedPatch,
} from '../utils/outbox-local.js';
import { getCurrentIdentityKey } from '../utils/outbox-identity.js';
// v0.172.0 — reload the pins when the service worker's background refresh
// finds the list changed under us (another device, the office).
import { subscribeApiUpdates, urlConcerns } from '../utils/apiFreshness.js';
import { pinEcho } from '../utils/pin-echo.js'; // 2026-08-20 a created defect must echo the pin it was sent
import { theme, btnPrimary, btnSecondary } from '../theme';
// v0.9.0 — trade catalogue drives the pin colour on the canvas (was
// status-based) so site users see a colour-coded plan at a glance.
import { tradeColor, tradeLabel } from '../utils/trades';
import { useActiveTrades } from '../utils/useTrades';
// v0.17.1 — Lucide migration.
import { MapPin, Download } from 'lucide-react';
import PinHalo, { PIN_HALO_CSS } from '../components/PinHalo';
// v0.38.1 — Free-tier defect cap at the drop-pin entry point.
import { isFreeTier, FREE_LIMITS } from '../utils/tier';
import UpgradePrompt from '../components/UpgradePrompt';
// v0.45.25 — Shared rename-while-uploading modal so the multi-file
// queue (forwarded from RegisterScreen's PlansManagerModal) walks
// each file through a rename step instead of silently uploading
// with the raw filename.
import PlanRenameModal from '../components/PlanRenameModal';
// v0.61.0 — Import a plan already saved on this Location in InSite
// (instead of only uploading a file). Picker modal; imports become
// pinnable drawings via /api/import-location-plan.
import ImportFromInSiteModal from '../components/ImportFromInSiteModal';
// 2026-10-29 (v0.263.0) — the pin card, shared with the register's ⋮ Edit details.
import DefectEditorSheet from '../components/DefectEditorSheet';
import { splitDefectForm, queueDefectPhoto, saveDefectEdit, deleteDefect } from '../utils/saveDefectEdit.js';

// v0.79 — Plan markup + measure toolset. Self-contained src/markup module,
// built on the shared measurement engine (@insitebg/plan-markup-core).
import { useMarkup, MarkupLayer, MarkupToolbar, CalibrationDialog, MarkupTotalsPanel } from '../markup';
// 2026-08-17 — which catalogue set the markup box offers. Its own module so the
// rule is testable and sits next to the opposite rule in utils/useTrades.js.
import { offerableTypes } from '../markup/pickMarkSet.js';
import { markupRemoteFor } from '../utils/markupApi';
import { PencilRuler } from 'lucide-react';
import { pdfPageToPng, warmPdfEngine } from '../utils/pdfRaster';

// PlanScreen — DropPin's drop-pin defect-on-drawing screen.
//
// v0.9.0 — trade-coloured pins. Pin background now reads tradeColor(d.trade)
//          from src/utils/trades.js (was status-based). DefectBottomSheet
//          gains a Trade swatch row picker. defectFromApi() carries the trade
//          field. migration-003 added Defects.Trade NVARCHAR(40).
// v0.4.0 (DropPin port) — Ported from InSpec FieldDefectsPlan.jsx.
//   Stripped: projectCode prop (now uses useParams projectId), site
//   supervisor / subcontractor coupling (assignee is now free-text),
//   InSpec version constants, role gates, the "back to defects list"
//   parent-controlled callback, /api/field-defects/_health migration
//   probe, and the controlled-from-parent pendingPin handle. The
//   bottom-sheet detail form is included inline (was rendered by
//   FieldDefects.jsx in the source).
//
//   Sellable-from-day-one: no hardcoded "InSite" branding, no role
//   gating, no tenant-specific shape. The backend defect / drawing
//   APIs (/api/drawings, /api/defects) don't exist yet, so all
//   network calls are stubbed with TODO_DROPPIN comments and either
//   return mock data or fall through gracefully.
//
//   Source revisions preserved inline as `// v2.11.x — …` so the
//   archaeology survives.

// ---------------------------------------------------------------------------
// Spinner — small inline busy indicator. Same as the InSpec source but
// re-themed to DropPin orange. v2.11.75 (InSpec) + v0.4.0 (DropPin port).
function Spinner({ label }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, color: theme.textLight }}>
      <span style={{
        width: 14, height: 14, borderRadius: '50%',
        border: `2px solid ${theme.border}`, borderTopColor: theme.brand,
        animation: 'fdpSpin 0.8s linear infinite', display: 'inline-block',
      }} />
      {label || 'Loading…'}
    </span>
  );
}

// ---------------------------------------------------------------------------
// API <-> UI shape bridges (v0.6.0). The new /api/drawings + /api/defects
// endpoints use camelCase modern field names; the original FieldDefectsPlan
// render code is still wired to InSpec's PascalCase shape (Id, Title,
// FileName, FileDataUrl, DrawingNumber, Revision, Discipline). Bridging at
// the fetch boundary lets us keep render code untouched.
function drawingFromApi(row) {
  if (!row) return row;
  return {
    Id: row.id ?? row.Id,
    Title: row.name ?? row.Title ?? '',
    DrawingNumber: row.drawingNumber ?? row.DrawingNumber ?? '',
    Revision: row.revision ?? row.Revision ?? '',
    Discipline: row.discipline ?? row.Discipline ?? '',
    FileName: row.fileName ?? row.FileName ?? row.sourceFileName ?? '',
    FileDataUrl: row.rasterUrl ?? row.sourceUrl ?? row.FileDataUrl ?? null,
    sourceUrl: row.sourceUrl,
    rasterUrl: row.rasterUrl,
    sourceMime: row.sourceMime,
    pageNumber: row.pageNumber,
    width: row.width,
    height: row.height,
    projectId: row.projectId,
  };
}
function defectFromApi(row) {
  if (!row) return row;
  return {
    id: row.id ?? row.Id,
    projectId: row.projectId,
    drawingId: row.drawingId ?? null,
    defectNumber: row.defectNumber,
    description: row.description ?? '',
    priority: row.priority ?? 'medium',
    status: row.status ?? 'open',
    // v0.45.1 — Type column for the unified register
    //           (defect/rfi/snag/observation). Backfill defaults
    //           to 'defect' for any pre-migration row.
    type: row.type ?? 'defect',
    assignee: row.assignedTo ?? row.assignee ?? '',
    assignedTo: row.assignedTo ?? null,
    assignedContactId: row.assignedContactId ?? null,
    locationLabel: row.locationLabel ?? null,
    trade: row.trade ?? 'general',
    markX: row.markX,
    markY: row.markY,
    // v0.45.1 — DefectPins child rows + count. pins[0] is the primary
    // (mirrors drawingId/markX/markY). pinCount > 1 ⇒ multi-pinned.
    pins: Array.isArray(row.pins) ? row.pins : [],
    pinCount: typeof row.pinCount === 'number' ? row.pinCount : (Array.isArray(row.pins) ? row.pins.length : 0),
    photos: Array.isArray(row.photos) ? row.photos : [],
    photoCount: typeof row.photoCount === 'number' ? row.photoCount : (Array.isArray(row.photos) ? row.photos.length : 0),
    rectifiedAt: row.rectifiedAt ?? null,
    // v0.171.0 — site mode: queued on this device, not yet on the server.
    pending: !!row.pending,
    pendingPhotoCount: Number(row.pendingPhotoCount) || 0,
  };
}

// v0.261.0 — queuedPhotoView (a queued photo from its own bytes) moved to
// utils/queuedPhotoView.js; the register's photo sheet uses it too.

// ---------------------------------------------------------------------------
// PlanScreen — the actual route component.
//
// Routes:
//   /projects/:projectId/plan                  — picker (auto-picks first)
//   /projects/:projectId/plan/:drawingId       — opens a specific drawing
//
// Pan/zoom/pin coords semantics (lifted verbatim from FieldDefectsPlan):
//   • Pin coords stored as normalised (0..1) markX/markY in
//     natural-image space — completely independent of zoom/pan.
//   • Pins render in the same transformed stage as the image, so they
//     zoom/pan in lockstep, then counter-scale themselves so the glyph
//     stays a constant on-screen size.
export default function PlanScreen() {
  // 2026-08-14 — the tenant's own list (Settings -> Mark types), falling back
  // to the built-in trades. A hook so this re-renders when the catalogue loads.
  const TRADES = useActiveTrades();

  const { projectId, drawingId: routeDrawingId } = useParams();
  const navigate = useNavigate();
  // v0.22.1 — Carry reportId scope from the URL through to /api/drawings
  // + /api/defects requests so this screen shows only the active
  // report's plans and pins.
  const [searchParams] = useSearchParams();
  const reportId = searchParams.get('reportId');

  // Drawings list + currently-picked drawing.
  const [drawings, setDrawings] = useState([]);
  const [loadingDrawings, setLoadingDrawings] = useState(true);
  const [pickedDrawingId, setPickedDrawingId] = useState(
    routeDrawingId ? Number(routeDrawingId) : null
  );
  const [drawingFileUrl, setDrawingFileUrl] = useState(null);
  const [loadingFile, setLoadingFile] = useState(false);
  const [error, setError] = useState('');

  // Defects list (was a prop in InSpec — fetched here as a route component).
  const [defects, setDefects] = useState([]);
  // Highlighted pin on arrival.
  // v0.45.8 — Wired to the URL hash `#defect-N` so RegisterScreen's
  //           row pin button can deep-link to a specific pin and the
  //           canvas zooms / pulses on arrival. Brett: "When i click
  //           a pin it should zoom into the pin location on the plan."
  //           The existing zoom + scroll-into-view effects (lines
  //           ~461 and ~592) already key on focusedDefectId — they
  //           just had no input until now.
  const [focusedDefectId, setFocusedDefectId] = useState(() => {
    try {
      const m = /^#defect-(\d+)$/.exec(window.location.hash || '');
      return m ? Number(m[1]) : null;
    } catch { return null; }
  });
  // Re-read on hash change so a click-then-back-then-click flow still
  // re-triggers the zoom rather than going stale.
  useEffect(() => {
    const onHashChange = () => {
      const m = /^#defect-(\d+)$/.exec(window.location.hash || '');
      setFocusedDefectId(m ? Number(m[1]) : null);
    };
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, []);

  // v2.11.77 — pending pin renders on the plan as soon as the user
  // taps, before the bottom-sheet form is filled in. DropPin runs the
  // bottom sheet locally so we own the state directly.
  const [pendingPin, setPendingPin] = useState(null);
  // v0.38.1 — Project-wide defect count (loaded once on mount) +
  //           UpgradePrompt state for free-tier cap hits at drop-pin time.
  const [projectDefectCount, setProjectDefectCount] = useState(null);
  // v0.39.1 — Project contacts feed the DefectBottomSheet Assignee picker.
  const [projectContacts, setProjectContacts] = useState([]);
  const [upgradeReason, setUpgradeReason] = useState(null);
  // Bottom-sheet open state — when truthy, the form is filled in for
  // either a new defect (pendingPin set) or editing an existing one
  // (editingDefect set).
  const [sheetOpen, setSheetOpen] = useState(false);
  const [editingDefect, setEditingDefect] = useState(null);
  // 2026-09-15 — each opening of the card is its own card: switching straight
  // from one pin to another rebuilds it, so it never shows the last pin's fields.
  const [sheetSeq, setSheetSeq] = useState(0);

  // Stage / image state.
  const stageRef = useRef(null);          // outer fixed-size container
  const imageRef = useRef(null);          // <img>
  const [scale, setScale] = useState(1);
  const [tx, setTx] = useState(0);
  const [ty, setTy] = useState(0);
  const [naturalW, setNaturalW] = useState(0);
  const [naturalH, setNaturalH] = useState(0);
  const [stageRect, setStageRect] = useState({ w: 0, h: 0 });
  // The canvas fills the window from its own top edge down. Re-measured on
  // resize and on orientation change; nothing above it is assumed, so adding or
  // removing a toolbar can never leave a band of dead space under the plan.
  const [stageH, setStageH] = useState(0);
  const STAGE_BOTTOM_GUTTER = 16;
  const STAGE_MIN_H = 320;
  useEffect(() => {
    const measure = () => {
      const el = stageRef.current;
      if (!el) return;
      const top = el.getBoundingClientRect().top;
      setStageH(Math.max(STAGE_MIN_H, Math.round(window.innerHeight - top - STAGE_BOTTOM_GUTTER)));
    };
    measure();
    // A second pass after layout settles — on first paint the toolbars above may
    // not have their final height, which would leave the stage short.
    const t = setTimeout(measure, 120);
    window.addEventListener('resize', measure);
    window.addEventListener('orientationchange', measure);
    // Anything ABOVE the stage changing height moves its top edge — the trade
    // legend wrapping, the armed-trade banner appearing, the offline strip. None
    // of those fire a window resize, so watch the document instead. The stage is
    // the last element, so its own height change does not move its own top: this
    // settles in one pass rather than looping.
    let ro = null;
    if (typeof ResizeObserver !== 'undefined' && document.body) {
      ro = new ResizeObserver(measure);
      ro.observe(document.body);
    }
    return () => {
      clearTimeout(t);
      window.removeEventListener('resize', measure);
      window.removeEventListener('orientationchange', measure);
      if (ro) ro.disconnect();
    };
  }, []);
  // Live mirrors of the transform, so a drag started from outside React's render
  // (the markup overlay's pan hand-off) reads the CURRENT values, not the ones
  // captured when its callback was built.
  const txRef = useRef(0), tyRef = useRef(0), scaleRef = useRef(1);
  useEffect(() => { txRef.current = tx; }, [tx]);
  useEffect(() => { tyRef.current = ty; }, [ty]);
  useEffect(() => { scaleRef.current = scale; }, [scale]);

  // Drag tracking (mouse + single-finger touch).
  const dragRef = useRef({ active: false, startX: 0, startY: 0, baseTx: 0, baseTy: 0, moved: false });
  // v2.18.18 — drag a saved pin to a new spot.
  const pinDragRef = useRef({ active: false, id: null, drawingId: null, startNormX: 0, startNormY: 0, didMove: false, oldNormX: 0, oldNormY: 0 });
  const [pinDragLive, setPinDragLive] = useState(null);
  const [pinMove, setPinMove] = useState(null);
  const [pinSaving, setPinSaving] = useState(false);
  // v0.9.1 — armed-trade lets the user click a chip in the legend then
  // tap the plan to drop a pin pre-coloured for that trade. Persists
  // across drops until the user clicks the chip again (toggle).
  const [armedTrade, setArmedTrade] = useState(null);
  // v0.45.5 — addPinFor state removed. Multi-pin (one defect spans
  // multiple drawings) is an RFI/snag workflow that belongs on
  // InSpec, not DropPin's defect system. Schema kept for future
  // integration; the UI plumbing is gone.
  // v0.9.1 — gate pin rendering until the plan image has actually
  // painted. Without this, when the dropdown switches drawings the
  // previous plan's pins flash over the new (still-loading) image
  // because pinsOnPicked re-keys before <img> swaps in.
  const [imageLoaded, setImageLoaded] = useState(false);
  // Pinch tracking.
  const pinchRef = useRef({ active: false, dist: 0, midX: 0, midY: 0, baseScale: 1, baseTx: 0, baseTy: 0 });
  // Double-tap tracking.
  const lastTapRef = useRef(0);

  // v2.11.75 — drag-and-drop a PDF or image directly onto the plan.
  const [dropping, setDropping] = useState(false);
  const [uploading, setUploading] = useState(false);
  // v0.61.0 — "Import from InSite" picker modal open state.
  const [importFromInSiteOpen, setImportFromInSiteOpen] = useState(false);

  // v0.79 — Plan markup + measure. `markupEnabled` toggles the overlay; the
  // useMarkup controller owns the annotation doc (per-drawing, persisted).
  const [markupEnabled, setMarkupEnabled] = useState(false);
  // v0.80 — optional server persistence (per drawing). markupRemoteFor returns
  // null when there is no drawing to key on, so useMarkup stays localStorage-only
  // in that case; a deployed /api/markup layers cross-device persistence on top
  // (localStorage remains the offline cache + fallback if the API is unreachable).
  const markupRemote = useMemo(() => markupRemoteFor(pickedDrawingId), [pickedDrawingId]);
  // 2026-08-14 — the tenant's own mark types (Settings -> Mark types). Fetched
  // once when markup is first opened; absent or unreachable simply means the
  // toolbar shows its built-in tools, exactly as before.
  const [markTypes, setMarkTypes] = useState([]);
  useEffect(() => {
    if (!markupEnabled || markTypes.length) return;
    let cancelled = false;
    fetch('/api/mark-catalogue', { credentials: 'same-origin' })
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => {
        if (cancelled || !j || !Array.isArray(j.sets)) return;
        // 2026-08-17 — the markup box offers the MEASURING set, not the pin
        // vocabulary. It used to take the first set with any active type; the
        // trades set is first, so the box listed trades. See pickMarkSet.js
        // for why the two readers of this catalogue want opposite things.
        setMarkTypes(offerableTypes(j.sets));
      })
      .catch(() => { /* the built-in tools are a fine fallback */ });
    return () => { cancelled = true; };
  }, [markupEnabled, markTypes.length]);

  const markup = useMarkup({
    active: markupEnabled,
    storageKey: (pickedDrawingId != null ? `${projectId}:${pickedDrawingId}` : null),
    remote: markupRemote,
  });
  // Convert a client point -> natural-image PIXEL space using the SAME
  // transform the pins use (imgPt = (screen - translate) / scale), so markup
  // registers pixel-exact with the plan under pan/zoom.
  const clientToImagePx = useCallback((clientX, clientY) => {
    const rect = stageRef.current?.getBoundingClientRect();
    if (!rect || !scale) return null;
    return { x: (clientX - rect.left - tx) / scale, y: (clientY - rect.top - ty) / scale };
  }, [tx, ty, scale]);

  const fileToDataUrl = (file) => new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result));
    r.onerror = () => reject(new Error('Read failed for ' + file.name));
    r.readAsDataURL(file);
  });

  // -------------------------------------------------------------------------
  // Drop-on-stage handler — auto-converts PDF → PNG, then POSTs a new
  // drawing row and switches to it.
  // DONE v0.6.0: /api/drawings is live — handleFileDrop POSTs the new
  // row and switches to it. Pre-v0.6.0 this was a local mock fallback.
  const handleFileDrop = useCallback(async (file) => {
    if (!file) return;
    setUploading(true);
    setError('');
    try {
      let fileName = file.name;
      let dataUrl;
      const isPdf = /\.pdf$/i.test(file.name) || file.type === 'application/pdf';
      const isImg = /^image\//.test(file.type) || /\.(png|jpe?g|gif|webp)$/i.test(file.name);
      if (isPdf) {
        dataUrl = await pdfPageToPng(file, { page: 1, scale: 2 });
        fileName = file.name.replace(/\.pdf$/i, '') + ' (p1).png';
      } else if (isImg) {
        dataUrl = await fileToDataUrl(file);
      } else {
        throw new Error('Drop a PDF or an image (PNG / JPG). Got: ' + (file.type || file.name));
      }
      const Title = file.name.replace(/\.[^.]+$/, '').slice(0, 200);

      // DONE v0.6.0: real POST /api/drawings. We send the converted PNG
      // (data URL) as rasterUrl so the next viewer can use it directly,
      // and the original file name + mime as source metadata.
      let row;
      try {
        const created = await apiSend('POST', '/api/drawings', {
          projectId: Number(projectId),
          name: Title,
          sourceUrl: dataUrl,
          rasterUrl: dataUrl,
          sourceMime: isPdf ? 'application/pdf' : (file.type || 'image/png'),
          pageNumber: isPdf ? 1 : null,
        });
        row = drawingFromApi(created);
      } catch (e) {
        // Network/backend failure — alert and bail so the user knows the
        // drop didn't persist. (Was a silent local mock pre-v0.6.0.)
        alertFailure('Could not create drawing: ' + (e.message || e), e);
        throw e;
      }

      // Refresh the list so the new row appears in the picker.
      try {
        const ld = await apiGet((reportId ? `/api/drawings?reportId=${encodeURIComponent(reportId)}` : `/api/drawings?projectId=${encodeURIComponent(projectId)}`));
        const arr = Array.isArray(ld) ? ld.map(drawingFromApi) : [];
        setDrawings(arr);
      } catch (_) { /* keep current list */ }

      setPickedDrawingId(row.Id);
      // Cache the data URL directly so the file-load effect doesn't have
      // to re-fetch it.
      setDrawingFileUrl(dataUrl);
    } catch (e) {
      // TODO_DROPPIN: replace with toast once we add a toaster.
      alertFailure('Drop failed: ' + (e.message || e), e);
      setError(String(e.message || e));
    } finally {
      setUploading(false);
      setDropping(false);
    }
  }, [projectId]);

  const onStageDragOver  = (e) => { e.preventDefault(); e.stopPropagation(); setDropping(true); };
  const onStageDragLeave = (e) => { e.preventDefault(); e.stopPropagation(); setDropping(false); };
  const onStageDrop      = (e) => {
    e.preventDefault(); e.stopPropagation();
    setDropping(false);
    const file = e.dataTransfer?.files?.[0];
    if (file) handleFileDrop(file);
  };

  // v0.34.0 — Pick up files queued by PlansManagerModal (the modal
  // on RegisterScreen drops files onto window.__droppin_pending_plans__
  // and navigates here).
  // v0.45.25 — Brett: "I just uploaded 2 drawings and it took me
  // straight to the plan page and skipped the loading two files page
  // where i could rename them." The previous handler ran a silent
  // for-loop with handleFileDrop and never showed the rename modal.
  // Replaced with a planQueue + PlanRenameModal pattern (mirrors the
  // ProjectDetailScreen upload flow) so the user names each file as
  // it's uploaded.
  useEffect(() => {
    const queued = typeof window !== 'undefined' ? window.__droppin_pending_plans__ : null;
    if (!Array.isArray(queued) || queued.length === 0) return;
    try { window.__droppin_pending_plans__ = null; } catch (_) {}
    // Filter to supported types up-front so the modal queue length
    // matches what we'll actually upload.
    const valid = [];
    for (const file of queued) {
      const isPdf = /\.pdf$/i.test(file.name) || file.type === 'application/pdf';
      const isImg = /^image\//.test(file.type) || /\.(png|jpe?g|gif|webp)$/i.test(file.name);
      if (!isPdf && !isImg) continue;
      const defaultName = file.name.replace(/\.[^.]+$/, '').slice(0, 200);
      valid.push({ file, defaultName });
    }
    if (valid.length) setPlanQueue(valid);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // v0.45.25 — planQueue + uploader for the rename-while-uploading
  // modal. Each confirm advances the queue; cancelling drops the
  // entire remainder. Mirrors ProjectDetailScreen's planQueue logic
  // so behaviour is identical between the two upload entry points.
  const [planQueue,     setPlanQueue]     = useState([]);
  const [planUploading, setPlanUploading] = useState(false);

  const uploadCurrentPlanQueued = async (nickname) => {
    const head = planQueue[0];
    if (!head) return;
    // v0.45.27 — Free-tier drawings-per-report cap. Brett: "i just
    // get a loading plan…i presume its locked." Was: this handler
    // had no client-side tier gate at all (added in v0.45.25,
    // missing this check). Mirror ProjectDetailScreen's gate using
    // `drawings.length` (PlanScreen tracks the list, not a count).
    // When the gate fires, kill the ENTIRE queue and surface the
    // upgrade prompt — otherwise the rename modal stays open behind
    // it and the user keeps re-triggering the same gate.
    if (isFreeTier() && drawings.length >= FREE_LIMITS.drawingsPerReport) {
      setPlanQueue([]);
      setUpgradeReason('drawingsPerReport');
      return;
    }
    setPlanUploading(true);
    try {
      const { file, defaultName } = head;
      const isPdf = /\.pdf$/i.test(file.name) || file.type === 'application/pdf';
      let dataUrl;
      if (isPdf) dataUrl = await pdfPageToPng(file, { page: 1, scale: 2 });
      else       dataUrl = await fileToDataUrl(file);
      const created = await apiSend('POST', '/api/drawings', {
        projectId: Number(projectId),
        reportId:  reportId ? Number(reportId) : undefined,
        name: (nickname || '').trim() || defaultName,
        sourceFileName: file.name || null,
        sourceUrl: dataUrl,
        rasterUrl: dataUrl,
        sourceMime: isPdf ? 'application/pdf' : (file.type || 'image/png'),
        pageNumber: isPdf ? 1 : null,
      });
      const newId = created.id ?? created.Id ?? null;
      // Refresh the picker so the new row appears.
      try {
        const ld = await apiGet((reportId ? `/api/drawings?reportId=${encodeURIComponent(reportId)}` : `/api/drawings?projectId=${encodeURIComponent(projectId)}`));
        const arr = Array.isArray(ld) ? ld.map(drawingFromApi) : [];
        setDrawings(arr);
      } catch (_) { /* keep current list */ }
      if (newId) setPickedDrawingId(newId);
      // Pop head — remaining files keep the modal open.
      setPlanQueue(prev => prev.slice(1));
    } catch (e) {
      alertFailure('Plan upload failed: ' + (e.message || e), e);
    } finally {
      setPlanUploading(false);
    }
  };
  const cancelPlanQueue   = () => { if (!planUploading) setPlanQueue([]); };
  const skipCurrentPlan   = () => { if (!planUploading) setPlanQueue(prev => prev.slice(1)); };

  // -------------------------------------------------------------------------
  // Pull the PDF engine down as soon as this screen opens, so it arrives while
  // the user is still picking a drawing rather than after they tap one. It then
  // lives in the service worker's cache, which is what lets a plan open on site
  // with no signal.
  useEffect(() => { warmPdfEngine(); }, []);

  // Load drawings for the project. v0.4.0 — uses apiGet + project-id
  // route param instead of InSpec's projectCode query string.
  // DONE v0.6.0: /api/drawings GET now live — results mapped via
  //   drawingFromApi to the UI's PascalCase shape.
  useEffect(() => {
    if (!projectId) return;
    let cancelled = false;
    setLoadingDrawings(true);
    (async () => {
      try {
        const list = await apiGet((reportId ? `/api/drawings?reportId=${encodeURIComponent(reportId)}` : `/api/drawings?projectId=${encodeURIComponent(projectId)}`));
        if (cancelled) return;
        const arr = Array.isArray(list) ? list.map(drawingFromApi) : [];
        setDrawings(arr);
        if (!routeDrawingId) {
          const pinnedDrawingIds = new Set((defects || []).map(d => d.drawingId).filter(Boolean));
          const isPdf = (d) => /\.pdf$/i.test(d.FileName || '') || /\.pdf$/i.test(d.Title || '');
          const isPlanLike = (d) => {
            const disc = String(d.Discipline || '').toLowerCase();
            const title = String(d.Title || '').toLowerCase();
            const num   = String(d.DrawingNumber || '').toLowerCase();
            if (/architectural|floor|plan/.test(disc)) return true;
            if (/floor|plan/.test(title)) return true;
            if (/^a[\d-]/i.test(num)) return true;
            return false;
          };
          const candidate =
               arr.find(d => pinnedDrawingIds.has(d.Id))
            || arr.find(d => /plan view drop/i.test(d.Discipline || ''))
            || arr.find(d => isPlanLike(d) && !isPdf(d))
            || arr.find(isPlanLike)
            || arr[0];
          if (candidate) setPickedDrawingId(candidate.Id);
        }
      } catch (e) {
        if (cancelled) return;
        // DONE v0.6.0: real failures now surface to the user instead of
        // a silent console.warn (was a TODO_DROPPIN stub).
        if (!isSessionEnded(e)) setError('Could not load drawings: ' + (e.message || e));
        setDrawings([]);
      } finally {
        if (!cancelled) setLoadingDrawings(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  // v0.39.1 — Project contacts for the Assignee dropdown.
  // v0.339.0 — the open REPORT's team (the team lives on reports now). With no
  // report open the Assignee list offers nobody from a team.
  useEffect(() => {
    if (!projectId || !reportId) { setProjectContacts([]); return undefined; }
    let cancelled = false;
    (async () => {
      try {
        const list = await apiGet(`/api/report-contacts?reportId=${encodeURIComponent(reportId)}`);
        if (!cancelled) setProjectContacts(Array.isArray(list) ? list : []);
      } catch { if (!cancelled) setProjectContacts([]); }
    })();
    return () => { cancelled = true; };
  }, [projectId, reportId]);

  // Load defects for the project. v0.4.0 — was a prop in InSpec.
  // DONE v0.6.0: /api/defects GET now live — results mapped via
  //   defectFromApi to bridge API camelCase to UI shape.
  // v0.172.0 — bumped by the service worker's API_UPDATED message; re-runs the
  // load below. The pending overlay is re-applied on every run, so a pin that
  // is still only on this phone survives the reload.
  const [defectsReloadTick, setDefectsReloadTick] = useState(0);
  useEffect(() => subscribeApiUpdates((url) => {
    if (urlConcerns(url, ['defects', 'defect-photos'])) setDefectsReloadTick(t => t + 1);
  }), []);
  useEffect(() => {
    if (!projectId) return;
    let cancelled = false;
    (async () => {
      try {
        let list;
        try {
          list = await apiGet((reportId ? `/api/defects?reportId=${encodeURIComponent(reportId)}` : `/api/defects?projectId=${encodeURIComponent(projectId)}`));
        } catch (e) {
          // v0.171.0 — on site with nothing cached the server's list is out of
          // reach, but the pins dropped on THIS phone are not. Show those, and
          // only shout when the server actually answered with an error.
          if (cancelled) return;
          const rows = await listOutbox();
          const local = applyPendingToDefects([], rows, { projectId, currentKey: getCurrentIdentityKey() });
          if (cancelled) return;
          setDefects(local.map(defectFromApi));
          setProjectDefectCount(local.length || null);
          const offline = !e?.status || e.status === 503;
          if (offline) console.warn('[DropPin] could not load defects; showing what is on this device:', e?.message || e);
          else alertFailure('Could not load defects: ' + (e.message || e), e);
          return;
        }
        if (cancelled) return;
        // v0.171.0 — SITE MODE: lay the queue over the server's list, so a pin
        // dropped offline is on the plan before it has synced.
        const rows = await listOutbox();
        if (cancelled) return;
        const merged = applyPendingToDefects(Array.isArray(list) ? list : [], rows, { projectId, currentKey: getCurrentIdentityKey() });
        setDefects(merged.map(defectFromApi));
        // v0.38.1 — Also fetch the PROJECT-wide defect count for the
        //           free-tier cap check. In report-scoped mode the
        //           `list` above only covers this report, so we hit
        //           /api/defects?projectId=N as a separate count call.
        if (reportId) {
          try {
            const all = await apiGet(`/api/defects?projectId=${encodeURIComponent(projectId)}`);
            if (!cancelled) setProjectDefectCount(Array.isArray(all) ? all.length : 0);
          } catch { if (!cancelled) setProjectDefectCount(null); }
        } else {
          setProjectDefectCount(Array.isArray(list) ? list.length : 0);
        }
      } catch (e) {
        if (cancelled) return;
        // DONE v0.6.0: surface load errors instead of silent fallback.
        alertFailure('Could not load defects: ' + (e.message || e), e);
        setDefects([]);
      }
    })();
    return () => { cancelled = true; };
  }, [projectId, defectsReloadTick]);

  // -------------------------------------------------------------------------
  // v0.171.0 — keep the plan honest about what has and has not synced. When a
  // queued create replays, the local pin takes the server's id (and the photos
  // queued behind it already point at that id — the drain rewrote them); when
  // rows come and go (photo queued, sync applied, row discarded) the pending
  // marks follow. Site mode is only bearable if the plan tells the truth.
  useEffect(() => {
    if (!projectId) return undefined;
    let alive = true;
    let timer = null;
    // The id swap is immediate; the re-read of the queue is debounced so a
    // forty-photo sync (two events per row) is a handful of reads, not eighty.
    const reconcile = () => {
      clearTimeout(timer);
      timer = setTimeout(async () => {
        const rows = await listOutbox();
        if (!alive) return;
        setDefects(prev => reconcilePending(prev, rows, { projectId, currentKey: getCurrentIdentityKey() }, defectFromApi));
      }, 150);
    };
    const unsub = subscribeOutbox((evt) => {
      if (evt && evt.type === 'progress') return;
      if (evt && evt.type === 'applied'
          && String(evt.method).toUpperCase() === 'POST'
          && /^\/api\/defects(\?|$)/.test(String(evt.path || ''))
          && evt.response && (evt.response.id ?? evt.response.Id) != null) {
        const localId = localIdFor(evt.localId);
        const real = defectFromApi(evt.response);
        setDefects(prev => prev.map(d => (String(d.id) === localId ? { ...d, ...real, pending: false, pendingPhotoCount: 0 } : d)));
        setEditingDefect(prev => ((prev && String(prev.id) === localId) ? { ...prev, ...real } : prev));
      }
      reconcile();
    });
    return () => { alive = false; clearTimeout(timer); unsub(); };
  }, [projectId]);

  // -------------------------------------------------------------------------
  // Focused-defect → auto-pick its drawing.
  useEffect(() => {
    if (!focusedDefectId || !defects?.length) return;
    const d = defects.find(x => String(x.id) === String(focusedDefectId));
    if (d?.drawingId) setPickedDrawingId(d.drawingId);
  }, [focusedDefectId, defects]);

  // -------------------------------------------------------------------------
  // site app v1.17.0 (InSpec) + v0.4.0 (DropPin port) — Auto-convert PDF
  // to PNG on drawing select so the user can drop pins immediately.
  // DONE v0.6.0: /api/drawings/:id GET + PATCH are live. GET hydrates
  // the row; PATCH stamps back the converted PNG raster so subsequent
  // opens skip the PDF render.
  useEffect(() => {
    if (!pickedDrawingId) { setDrawingFileUrl(null); setImageLoaded(false); return; }
    // v0.9.1 — flip imageLoaded false the instant we start loading a new
    // drawing so the old pins stop rendering before the new <img> mounts.
    setImageLoaded(false);
    // If we already have a data URL cached (e.g. from a fresh drop)
    // skip the round-trip.
    const local = drawings.find(d => Number(d.Id) === Number(pickedDrawingId));
    if (local?.FileDataUrl && !drawingFileUrl) {
      // The data URL is already in memory — render it directly. (Note we
      // still hit the auto-PDF-convert branch below if it's a PDF.)
    }
    let cancelled = false;
    setLoadingFile(true);
    setError('');
    (async () => {
      try {
        // DONE v0.6.0: real GET /api/drawings/:id — map to UI shape.
        //   Fall back to the local row if the network blip-fails so the
        //   user can still re-open a freshly-dropped drawing.
        let row;
        try {
          const fetched = await apiGet(`/api/drawings/${pickedDrawingId}`);
          row = drawingFromApi(fetched);
        } catch (e) {
          console.warn('[DropPin] /api/drawings/:id fetch failed, using local cache:', e.message);
          row = local || null;
        }
        if (cancelled) return;
        if (!row?.FileDataUrl) {
          if (!local?.FileDataUrl) {
            setError('Drawing has no file attached.');
            setDrawingFileUrl(null);
            setLoadingFile(false);
            return;
          }
          row = local;
        }
        const url = row.FileDataUrl;
        const isPdf = typeof url === 'string' && (url.startsWith('data:application/pdf') || /\.pdf(\?|#|$)/i.test(url));
        if (!isPdf) {
          setDrawingFileUrl(url);
          setLoadingFile(false);
          return;
        }
        try {
          const png = await pdfPageToPng(url, { page: 1, scale: 2 });
          if (cancelled) return;
          setDrawingFileUrl(png);
          setLoadingFile(false);
          const newName = (row.FileName || 'drawing').replace(/\.pdf$/i, '') + ' (p1).png';
          // DONE v0.6.0: PATCH the converted PNG raster back so the next
          //   viewer gets it directly. Fire-and-forget; a failure here
          //   isn't user-facing because the in-memory PNG still renders.
          apiSend('PATCH', `/api/drawings/${pickedDrawingId}`, {
            rasterUrl: png,
          }).catch((e) => { console.warn('[DropPin] raster PATCH failed:', e.message); });
        } catch (e) {
          if (cancelled) return;
          setError('PDF conversion failed: ' + (e.message || String(e)));
          setDrawingFileUrl(url);
          setLoadingFile(false);
        }
      } catch (e) {
        if (!cancelled) {
          setError('Drawing load failed: ' + e.message);
          setLoadingFile(false);
        }
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pickedDrawingId]);

  // -------------------------------------------------------------------------
  // Measure the stage itself. A ResizeObserver, not a window listener: the stage
  // now sets its own height (see stageH above) and the content column widens on
  // the full-bleed layout, neither of which fires a window resize — the old
  // listener would have left stageRect stale, and clampPan + FIT read it.
  useEffect(() => {
    const el = stageRef.current;
    if (!el) return undefined;
    const measure = () => setStageRect({ w: el.clientWidth, h: el.clientHeight });
    measure();
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', measure);
      return () => window.removeEventListener('resize', measure);
    }
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Fit-to-stage when image dimensions / stage size change.
  // v0.45.26 — Brett: "can we zoom the plan up a bit by default."
  // Multiply the pure fit scale by INITIAL_ZOOM_BOOST so the drawing
  // fills the canvas tighter on first load. The FIT button still
  // resets to true fit (1.0× of the math) via the same calc.
  const INITIAL_ZOOM_BOOST = 1.20;
  const fitToStage = useCallback((boost = INITIAL_ZOOM_BOOST) => {
    if (!naturalW || !naturalH || !stageRect.w || !stageRect.h) return;
    const sx = stageRect.w / naturalW;
    const sy = stageRect.h / naturalH;
    const pureFit = Math.min(sx, sy);
    const fit = pureFit * boost;
    setScale(fit);
    setTx((stageRect.w - naturalW * fit) / 2);
    setTy((stageRect.h - naturalH * fit) / 2);
  }, [naturalW, naturalH, stageRect.w, stageRect.h]);

  const onImgLoad = () => {
    const el = imageRef.current;
    if (!el) return;
    setNaturalW(el.naturalWidth || el.width);
    setNaturalH(el.naturalHeight || el.height);
    // v0.9.1 — image has painted; safe to render pins.
    setImageLoaded(true);
  };

  // FIT FOLLOWS THE WINDOW UNTIL YOU TAKE OVER.
  //
  // It used to re-fit on every stageRect change, which threw away the
  // zoom-to-pin the register had just asked for. Pinning it to once-per-drawing
  // fixed that but broke the opposite case: the stage now measures itself and
  // grows ~120ms after first paint, so a one-shot fit sized the plan to the
  // SMALL stage and left it small in a big canvas — which is what Brett was
  // looking at. So: keep re-fitting while the view is still untouched, and stop
  // the moment the user zooms, pans, or is sent to a pin.
  const userAdjustedRef = useRef(false);
  useEffect(() => { userAdjustedRef.current = false; }, [pickedDrawingId]);
  useEffect(() => {
    if (!naturalW || !naturalH || !stageRect.w || !stageRect.h) return;
    if (userAdjustedRef.current) return;
    fitToStage();
  }, [fitToStage, pickedDrawingId, naturalW, naturalH, stageRect.w, stageRect.h]);

  // v2.11.99/2.12.1 (InSpec) — Auto-zoom into the focused pin.
  useEffect(() => {
    if (!focusedDefectId || !defects?.length) return;
    const d = defects.find(x => String(x.id) === String(focusedDefectId));
    if (!d || d.markX == null || d.markY == null) return;
    if (Number(d.drawingId) !== Number(pickedDrawingId)) return;
    if (!naturalW || !naturalH || !stageRect.w || !stageRect.h) return;
    // v0.139.0 — Brett: "go to the pin if we select the pin and zoom in on the
    // pin." 1.25× was barely a zoom on a big sheet; 2× actually lands you on it,
    // and never zooms you back OUT if you were already in closer.
    userAdjustedRef.current = true;   // sent to a pin — a later re-fit must not undo it
    const targetScale = Math.max(2, scaleRef.current);
    const cx = Number(d.markX) * naturalW;
    const cy = Number(d.markY) * naturalH;
    const newTx = (stageRect.w / 2) - targetScale * cx;
    const newTy = (stageRect.h / 2) - targetScale * cy;
    const [ctx2, cty2] = clampPan(newTx, newTy, targetScale);
    setScale(targetScale);
    setTx(ctx2);
    setTy(cty2);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusedDefectId, pickedDrawingId, naturalW, naturalH, stageRect.w, stageRect.h]);

  // -------------------------------------------------------------------------
  // Clamp + zoom helpers (lifted verbatim).
  const clampPan = useCallback((nextTx, nextTy, nextScale) => {
    if (!naturalW || !naturalH || !stageRect.w || !stageRect.h) return [nextTx, nextTy];
    const w = naturalW * nextScale;
    const h = naturalH * nextScale;
    const minVisX = Math.min(w, stageRect.w) * 0.25;
    const minVisY = Math.min(h, stageRect.h) * 0.25;
    const minTx = -w + minVisX;
    const maxTx = stageRect.w - minVisX;
    const minTy = -h + minVisY;
    const maxTy = stageRect.h - minVisY;
    nextTx = Math.min(maxTx, Math.max(minTx, nextTx));
    nextTy = Math.min(maxTy, Math.max(minTy, nextTy));
    return [nextTx, nextTy];
  }, [naturalW, naturalH, stageRect.w, stageRect.h]);

  const zoomAt = useCallback((factor, sx, sy) => {
    userAdjustedRef.current = true;   // you've taken the wheel — stop auto-fitting
    setScale(prev => {
      const nextScale = Math.min(20, Math.max(0.05, prev * factor));
      setTx(prevTx => {
        setTy(prevTy => {
          const imgPtX = (sx - prevTx) / prev;
          const imgPtY = (sy - prevTy) / prev;
          const newTx = sx - nextScale * imgPtX;
          const newTy = sy - nextScale * imgPtY;
          const [, cy] = clampPan(newTx, newTy, nextScale);
          return cy;
        });
        const imgPtX = (sx - prevTx) / prev;
        const newTxRaw = sx - nextScale * imgPtX;
        const [cx] = clampPan(newTxRaw, ty, nextScale);
        return cx;
      });
      return nextScale;
    });
  }, [clampPan, ty]);

  const onWheel = (e) => {
    e.preventDefault();
    const rect = stageRef.current?.getBoundingClientRect();
    if (!rect) return;
    const sx = e.clientX - rect.left;
    const sy = e.clientY - rect.top;
    const factor = e.deltaY < 0 ? 1.12 : 1 / 1.12;
    zoomAt(factor, sx, sy);
  };

  // -------------------------------------------------------------------------
  // Pointer-down — start drag. Window listeners so the drag follows the
  // cursor wherever it goes. Same logic as InSpec; window-listener
  // pattern intentionally chosen over setPointerCapture because the
  // <img> has pointer-events:none and capture silently fails.
  const onPointerDown = (e) => {
    if (e.target?.tagName === 'BUTTON') return;
    if (e.pointerType === 'touch' && e.isPrimary === false) return;
    if (pinchRef.current.active) return;
    dragRef.current = {
      active: true,
      startX: e.clientX,
      startY: e.clientY,
      baseTx: tx,
      baseTy: ty,
      moved: false,
      pointerId: e.pointerId,
      tapClientX: e.clientX,
      tapClientY: e.clientY,
    };
    const onWinMove = (ev) => {
      const d = dragRef.current;
      if (!d.active) return;
      const dx = ev.clientX - d.startX;
      const dy = ev.clientY - d.startY;
      if (Math.abs(dx) + Math.abs(dy) > 4) { d.moved = true; userAdjustedRef.current = true; }
      const [cx, cy] = clampPan(d.baseTx + dx, d.baseTy + dy, scale);
      setTx(cx);
      setTy(cy);
      ev.preventDefault?.();
    };
    const onWinUp = (ev) => {
      const d = dragRef.current;
      window.removeEventListener('pointermove', onWinMove);
      window.removeEventListener('pointerup',   onWinUp);
      window.removeEventListener('pointercancel', onWinUp);
      dragRef.current = { ...d, active: false };
      if (!d.moved) {
        const rect = stageRef.current?.getBoundingClientRect();
        if (rect && ev.clientX >= rect.left && ev.clientX <= rect.right &&
            ev.clientY >= rect.top && ev.clientY <= rect.bottom) {
          handleTap({ clientX: ev.clientX, clientY: ev.clientY });
        }
      }
      const now = Date.now();
      if (now - lastTapRef.current < 280 && !d.moved) {
        fitToStage(1);            // double-tap = the whole page, same as FIT
        lastTapRef.current = 0;
      } else {
        lastTapRef.current = now;
      }
    };
    window.addEventListener('pointermove', onWinMove);
    window.addEventListener('pointerup',   onWinUp);
    window.addEventListener('pointercancel', onWinUp);
  };

  const onPointerMove = () => {};
  const onPointerUp   = () => {};

  // v0.139.0 — start a plain pan drag from an arbitrary point. The markup overlay
  // calls this for middle-button / held-Space so you can walk the plan WITHOUT
  // losing the shape you're part-way through drawing. No tap fallback here:
  // this is a pan and nothing else, so it can never drop a pin.
  const beginPan = useCallback((clientX, clientY) => {
    userAdjustedRef.current = true;
    const startX = clientX, startY = clientY;
    const baseTx = txRef.current, baseTy = tyRef.current, s = scaleRef.current;
    const onWinMove = (ev) => {
      const [cx, cy] = clampPan(baseTx + (ev.clientX - startX), baseTy + (ev.clientY - startY), s);
      setTx(cx);
      setTy(cy);
      ev.preventDefault?.();
    };
    const onWinUp = () => {
      window.removeEventListener('pointermove', onWinMove);
      window.removeEventListener('pointerup', onWinUp);
      window.removeEventListener('pointercancel', onWinUp);
    };
    window.addEventListener('pointermove', onWinMove);
    window.addEventListener('pointerup', onWinUp);
    window.addEventListener('pointercancel', onWinUp);
  }, [clampPan]);

  // Convert a stage-local tap into normalised image coords (0..1).
  const stageToNorm = (clientX, clientY) => {
    const rect = stageRef.current?.getBoundingClientRect();
    if (!rect || !naturalW || !naturalH) return null;
    const sx = clientX - rect.left;
    const sy = clientY - rect.top;
    const imgPtX = (sx - tx) / scale;
    const imgPtY = (sy - ty) / scale;
    const nx = imgPtX / naturalW;
    const ny = imgPtY / naturalH;
    if (nx < 0 || nx > 1 || ny < 0 || ny > 1) return null;
    return { nx, ny };
  };

  // -------------------------------------------------------------------------
  // Begin a saved-pin drag.
  const beginPinDrag = (e, defect) => {
    // v0.9.1 — refuse drag while the new drawing image is still loading;
    // the saved pin's coords are normalised to the stale (or unset)
    // naturalW/H so the drag math is wrong.
    if (!imageLoaded) return;
    e.preventDefault();
    e.stopPropagation();
    pinDragRef.current = {
      active: true,
      id: defect.id,
      drawingId: defect.drawingId,
      startNormX: Number(defect.markX),
      startNormY: Number(defect.markY),
      oldNormX:   Number(defect.markX),
      oldNormY:   Number(defect.markY),
      didMove: false,
      lastClientX: e.clientX,
      lastClientY: e.clientY,
    };
    setPinDragLive({ id: defect.id, normX: Number(defect.markX), normY: Number(defect.markY) });
    const onMove = (ev) => {
      const d = pinDragRef.current;
      if (!d.active) return;
      const norm = stageToNorm(ev.clientX, ev.clientY);
      if (!norm) return;
      const dxClient = Math.abs(ev.clientX - d.lastClientX);
      const dyClient = Math.abs(ev.clientY - d.lastClientY);
      if (dxClient > 4 || dyClient > 4) d.didMove = true;
      setPinDragLive({ id: d.id, normX: norm.nx, normY: norm.ny });
    };
    const onUp = (ev) => {
      const d = pinDragRef.current;
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
      if (!d.active) return;
      d.active = false;
      if (!d.didMove) {
        setPinDragLive(null);
        const target = (defects || []).find(x => String(x.id) === String(d.id));
        if (target) focusAndOpenDefect(target);
        return;
      }
      const norm = stageToNorm(ev.clientX, ev.clientY) || { nx: d.startNormX, ny: d.startNormY };
      setPinDragLive(null);
      setPinMove({
        id: d.id,
        drawingId: d.drawingId,
        oldX: d.oldNormX, oldY: d.oldNormY,
        newX: Number(norm.nx.toFixed(5)),
        newY: Number(norm.ny.toFixed(5)),
      });
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup',   onUp);
    window.addEventListener('pointercancel', onUp);
  };

  // -------------------------------------------------------------------------
  // Save / cancel a moved pin.
  // DONE v0.6.0: real PATCH /api/defects/:id wired. We patch in-memory
  //   only after the server confirms so we don't show stale data on a
  //   500/permission error.
  const savePinMove = async () => {
    if (!pinMove) return;
    setPinSaving(true);
    try {
      const patch = { drawingId: pinMove.drawingId, markX: pinMove.newX, markY: pinMove.newY };
      let merged;
      if (isLocalId(pinMove.id)) {
        // v0.171.0 — the pin only exists in the queue: move it there.
        const ok = await updateOutboxItem(rowIdFromLocalId(pinMove.id), (row) => ({ ...row, body: mergeQueuedPatch(row.body, patch) }));
        if (!ok) throw new Error('This pin is no longer waiting to sync — reload the plan and try again.');
        merged = { ...patch, pending: true };
      } else {
        const updated = await apiSend('PATCH', `/api/defects/${pinMove.id}`, patch);
        // Held or offline: the { queued } marker has no coordinates. Keep ours
        // and mark the pin pending. (defectFromApi on the marker used to spread
        // `markX: undefined` over the pin and make it vanish.)
        merged = (updated && updated.queued) ? { ...patch, pending: true } : (defectFromApi(updated) || patch);
      }
      setDefects(prev => prev.map(d =>
        String(d.id) === String(pinMove.id) ? { ...d, ...merged } : d
      ));
      setPinMove(null);
    } catch (err) {
      // TODO_DROPPIN: toast instead of alert.
      alertFailure('Could not save new pin position: ' + err.message, err);
    } finally {
      setPinSaving(false);
    }
  };

  const cancelPinMove = () => setPinMove(null);

  // -------------------------------------------------------------------------
  // Tap on empty plan → drop a pending pin and open the bottom sheet
  // for fill-in.
  // v0.45.5 — Multi-pin handler removed; the function is back to the
  //           pre-v0.45.1 single-pin flow.
  const handleTap = async (e) => {
    if (!pickedDrawingId) return;
    // v0.38.1 — Free-tier per-project defect cap. Gate at drop-pin time
    //           so the user doesn't waste effort filling the sheet.
    if (isFreeTier() && projectDefectCount != null && projectDefectCount >= FREE_LIMITS.defectsPerProject) {
      setUpgradeReason('defectsPerProject');
      return;
    }
    const norm = stageToNorm(e.clientX, e.clientY);
    if (!norm) return;
    const coords = {
      drawingId: pickedDrawingId,
      markX: Number(norm.nx.toFixed(5)),
      markY: Number(norm.ny.toFixed(5)),
    };
    setPendingPin(coords);
    setEditingDefect(null);
    setSheetSeq(n => n + 1);
    setSheetOpen(true);
  };

  // Open an existing defect (from a saved-pin tap).
  const openDefect = (defect) => {
    setEditingDefect(defect);
    setPendingPin(null);
    setSheetSeq(n => n + 1);
    setSheetOpen(true);
  };

  // 2026-09-15 — ONE WAY TO GO TO A PIN: zoom it into view and open its card.
  // A tap on the pin, the arrow keys, and a tap on another pin while a card is
  // open all come here.
  const focusAndOpenDefect = (target) => {
    if (!target) return;
    try {
      userAdjustedRef.current = true;
      const targetScale = Math.max(scale, 1.25);
      const cx = Number(target.markX) * naturalW;
      const cy = Number(target.markY) * naturalH;
      const newTx = (stageRect.w / 2) - targetScale * cx;
      const newTy = (stageRect.h / 2) - targetScale * cy;
      const [ctx2, cty2] = clampPan(newTx, newTy, targetScale);
      setScale(targetScale);
      setTx(ctx2);
      setTy(cty2);
    } catch (_) {}
    openDefect(target);
  };

  // 2026-09-15 — THE PIN UNDER A TAP ON THE CARD'S BACKDROP. Brett: "if im in
  // a plan i cant click from pin to pin". The open card's backdrop covers the
  // whole plan, so a tap on another pin only closed the card. The backdrop now
  // asks what is under the tap.
  const pinAtPoint = (clientX, clientY) => {
    if (typeof document === 'undefined' || typeof document.elementsFromPoint !== 'function') return null;
    const hit = document.elementsFromPoint(clientX, clientY)
      .map(el => (el && typeof el.closest === 'function' ? el.closest('[data-pin-id]') : null))
      .find(Boolean);
    if (!hit) return null;
    const id = hit.getAttribute('data-pin-id');
    return pinsOnPicked.find(pin => String(pin.id) === String(id)) || null;
  };

  // -------------------------------------------------------------------------
  // Multi-touch pinch — same as InSpec.
  const touchPointsRef = useRef([]);
  const onTouchStart = (e) => {
    touchPointsRef.current = Array.from(e.touches);
    if (e.touches.length === 2) {
      const [a, b] = e.touches;
      const dist = distance({ x: a.clientX, y: a.clientY }, { x: b.clientX, y: b.clientY });
      const rect = stageRef.current?.getBoundingClientRect();
      const midX = (a.clientX + b.clientX) / 2 - (rect?.left || 0);
      const midY = (a.clientY + b.clientY) / 2 - (rect?.top || 0);
      pinchRef.current = { active: true, dist, midX, midY, baseScale: scale, baseTx: tx, baseTy: ty };
      // v2.11.93 — cancel one-finger drag so pinch wins and tap doesn't fire.
      dragRef.current.active = false;
      dragRef.current.moved = true;
    }
  };

  const onTouchMove = (e) => {
    if (e.touches.length === 2 && pinchRef.current.active) {
      e.preventDefault();
      userAdjustedRef.current = true;
      const [a, b] = e.touches;
      const dist = distance({ x: a.clientX, y: a.clientY }, { x: b.clientX, y: b.clientY });
      const factor = dist / (pinchRef.current.dist || 1);
      const nextScale = Math.min(20, Math.max(0.05, pinchRef.current.baseScale * factor));
      // v0.139.0 — TWO-FINGER DRAG PANS. The gesture used to zoom about a frozen
      // midpoint, so on a tablet there was no way to move around the plan while
      // a tool was armed (Brett: "i cant pan around the page while im measuring
      // an area"). Anchoring the zoom at the START midpoint and translating by
      // how far that midpoint has since moved gives pinch-zoom AND drag-pan in
      // the one gesture, whatever tool is active.
      const rect = stageRef.current?.getBoundingClientRect();
      const nowMidX = (a.clientX + b.clientX) / 2 - (rect?.left || 0);
      const nowMidY = (a.clientY + b.clientY) / 2 - (rect?.top || 0);
      const panDX = nowMidX - pinchRef.current.midX;
      const panDY = nowMidY - pinchRef.current.midY;
      const sx = pinchRef.current.midX;
      const sy = pinchRef.current.midY;
      const imgPtX = (sx - pinchRef.current.baseTx) / pinchRef.current.baseScale;
      const imgPtY = (sy - pinchRef.current.baseTy) / pinchRef.current.baseScale;
      const newTx = sx - nextScale * imgPtX + panDX;
      const newTy = sy - nextScale * imgPtY + panDY;
      const [cx, cy] = clampPan(newTx, newTy, nextScale);
      setScale(nextScale);
      setTx(cx);
      setTy(cy);
    }
  };

  const onTouchEnd = (e) => {
    if (e.touches.length < 2) pinchRef.current.active = false;
    touchPointsRef.current = Array.from(e.touches);
  };

  // Buttons.
  const zoomIn  = () => zoomAt(1.25, stageRect.w / 2, stageRect.h / 2);
  const zoomOut = () => zoomAt(1 / 1.25, stageRect.w / 2, stageRect.h / 2);
  // v0.139.0 — FIT means the WHOLE page. It used to inherit the first-load zoom
  // boost from the default parameter, so it cropped the edges off the drawing
  // (Brett: "The fit button is not fitting the entire page"). Pass 1 explicitly.
  // v0.140.0 — FIT also hands control back to the window: after it, resizing the
  // browser keeps the whole plan in view again, until you zoom or pan.
  const fit     = () => { userAdjustedRef.current = false; fitToStage(1); };

  // Keyboard nav — arrow keys cycle through pins on this drawing
  // (InSpec didn't have this; added per the brief's "keyboard nav
  // between pins" line). v0.4.0 (DropPin port).
  const pinsOnPicked = useMemo(
    () => (defects || []).filter(d => d.drawingId && d.markX != null && d.markY != null && Number(d.drawingId) === Number(pickedDrawingId)),
    [defects, pickedDrawingId]
  );
  // v0.171.0 — how many of those are still only on this phone.
  const pendingOnPicked = useMemo(() => pinsOnPicked.filter(d => d.pending).length, [pinsOnPicked]);

  useEffect(() => {
    const onKey = (ev) => {
      if (sheetOpen) return;
      if (!pinsOnPicked.length) return;
      const idx = pinsOnPicked.findIndex(p => String(p.id) === String(editingDefect?.id));
      let next = -1;
      if (ev.key === 'ArrowRight' || ev.key === 'ArrowDown') {
        next = (idx + 1 + pinsOnPicked.length) % pinsOnPicked.length;
      } else if (ev.key === 'ArrowLeft' || ev.key === 'ArrowUp') {
        next = (idx <= 0 ? pinsOnPicked.length - 1 : idx - 1);
      } else if (ev.key === 'Escape') {
        setSheetOpen(false); setPendingPin(null); setEditingDefect(null); return;
      } else return;
      const target = pinsOnPicked[next];
      if (target) focusAndOpenDefect(target);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [pinsOnPicked, editingDefect, sheetOpen, naturalW, naturalH, stageRect.w, stageRect.h, scale, clampPan]);

  // v0.14.0 - 3-state pin colour. Open=danger, Rectified=warn,
  // Complete=success. Legacy 'in_progress' folds into 'open' (still
  // not fixed yet); legacy 'closed' folds into 'complete'.
  const pinColour = (status) => {
    switch (String(status || '').toLowerCase()) {
      case 'complete': case 'closed':         return theme.success;
      case 'rectified':                       return theme.warn;
      default:                                return theme.danger;   // open / in_progress / new / unknown
    }
  };

  // -------------------------------------------------------------------------
  // Bottom-sheet handlers — create / update / delete defect.
  // DONE v0.6.0: /api/defects POST/PATCH/DELETE wired. The form arrives
  //   in the UI shape (description/priority/status/assignee/photos); we
  //   translate `assignee` → `assignedTo` on the way out and use
  //   defectFromApi on the way back. Photos are still client-only —
  //   see the TODO in DefectBottomSheet about /api/defect-photos.
  const onSheetSave = async (form) => {
    // 2026-10-29 (v0.263.0) — the network + outbox half of saving lives in
    // utils/saveDefectEdit.js, shared with the register's ⋮ Edit details. The
    // form's photos are split off there and queued as bytes behind the defect.
    const { apiPayload, newPhotos } = splitDefectForm(form);

    try {
      if (editingDefect) {
        // v0.171.0 — a pin that only exists in the queue has the edit folded
        // into its queued create (never a PATCH against an id the server has
        // not seen). Held (site mode) or offline, a PATCH is in the outbox:
        // show the edit as made and mark the pin pending. defectFromApi on the
        // { queued } marker used to spread undefined over every field.
        const { local, updated } = await saveDefectEdit(editingDefect, form);
        const merged = local
          ? { ...defectFromApi({ ...apiPayload, id: editingDefect.id, pending: true }), id: editingDefect.id, pending: true }
          : (updated && updated.queued)
            ? { ...defectFromApi({ ...editingDefect, ...apiPayload, assignee: apiPayload.assignedTo || '', id: editingDefect.id }), pending: true }
            : defectFromApi(updated);
        setDefects(prev => prev.map(d =>
          String(d.id) === String(editingDefect.id) ? { ...d, ...merged } : d
        ));
      } else if (pendingPin) {
        // POST new.
        const payload = {
          projectId: Number(projectId),
          drawingId: pendingPin.drawingId,
          markX: pendingPin.markX,
          markY: pendingPin.markY,
          ...apiPayload,
        };
        // 2026-08-20 — OFFLINE, THIS RETURNS A MARKER, NOT A DEFECT.
        //
        // Brett pinned 25 defects on a site with no reception. The defects
        // synced hours later; every photo was gone. `apiSend` queues the POST
        // and answers { queued: true, localId } — which has no `id`, so the
        // guard below was false, the loop never ran, and photos that only ever
        // existed in this screen's form state were dropped on the floor. The
        // work looked like it had synced, which is what made it invisible.
        //
        // The photos now queue behind the defect, pointing at it by reference;
        // the drain rewrites that to the real id once the defect is created.
        // See src/utils/outbox-refs.js.
        //
        // v0.171.0 — and the queued pin is a REAL PIN ON THE PLAN. It gets a
        // local id ('local:<row>') and draws at its coordinates, marked as not
        // synced. Until now the marker had no coordinates, nothing drew it,
        // and a pin dropped on site looked like it had not saved.
        const createdResp = await apiSend('POST', '/api/defects', payload);
        // 2026-08-20 — VERIFY THE PIN CAME BACK (Midland close-out). We sent
        // drawingId/markX/markY; if the created row echoes without them the
        // defect exists but is on no plan, and until today nothing said so.
        // The API now fails closed (api/shared/pin-fields.js) so this should
        // never fire — it is the alarm that reports the regression the moment
        // it happens instead of on site weeks later. Logic in pin-echo.js.
        const echo = pinEcho(payload, createdResp);
        if (echo.state === 'lost') alert(echo.message);
        const queuedDefectRef = (createdResp && createdResp.queued) ? createdResp.localId : null;
        const created = queuedDefectRef != null
          ? defectFromApi({ ...payload, id: localIdFor(queuedDefectRef), pending: true, pendingPhotoCount: newPhotos.length, photoCount: newPhotos.length })
          : defectFromApi(createdResp);
        const defectIdForPhotos = queuedDefectRef != null
          ? outboxRef(queuedDefectRef)
          : (created?.id != null ? Number(created.id) : null);
        // Upload any photos the user attached during create.
        if (defectIdForPhotos != null) {
          for (const p of newPhotos) await queueDefectPhoto(defectIdForPhotos, p);
        }
        setDefects(prev => [...prev, created]);
        // v0.38.1 — keep the project-wide count in sync with the cap check.
        setProjectDefectCount(n => (typeof n === 'number' ? n + 1 : n));
      }
    } catch (err) {
      // TODO_DROPPIN: toast instead of alert.
      alertFailure('Could not save defect: ' + err.message, err);
      // v0.287.0 — false keeps the card where it is: Previous / Next only
      // move once the save has landed.
      return false;
    }
    // v0.64 — upload queued full-size photos in the background (durable,
    // survives poor reception). v0.171.0 — in site mode this is a no-op: the
    // queue waits for Sync now / Wi-Fi (see outbox.js drainOutbox).
    drainOutbox();
    setSheetOpen(false);
    setPendingPin(null);
    setEditingDefect(null);
    return true;
  };

  // DONE v0.6.0: POST /api/defect-photos. Photos arrive as either a
  //   pre-uploaded {url} or a fresh client {dataUrl, name} — for now we
  //   pass the dataUrl straight through as `url`; once we have a blob /
  //   storage endpoint the dataUrl will be uploaded to that first.
  const uploadDefectPhoto = async (defectId, photo) => {
    const url = photo.url || photo.dataUrl;
    if (!url) return null;
    return await apiSend('POST', '/api/defect-photos', {
      defectId: Number(defectId),
      url,
      caption: photo.name || null,
    });
  };

  const onSheetDelete = async () => {
    if (!editingDefect) return;
    if (!confirm('Delete this defect? This can\'t be undone.')) return;
    try {
      // v0.171.0 — a pin that never left the phone is dropped from the queue
      // with everything queued behind it; anything else is a real DELETE.
      // 2026-10-29 (v0.263.0) — both live in utils/saveDefectEdit.js.
      const { local } = await deleteDefect(editingDefect);
      if (local) setProjectDefectCount(n => (typeof n === 'number' ? Math.max(0, n - 1) : n));
      setDefects(prev => prev.filter(d => String(d.id) !== String(editingDefect.id)));
    } catch (err) {
      alertFailure('Could not delete defect: ' + err.message, err);
      return;
    }
    setSheetOpen(false);
    setEditingDefect(null);
  };

  const onSheetCancel = () => {
    setSheetOpen(false);
    setPendingPin(null);
    setEditingDefect(null);
  };

  // ===========================================================================
  // Render.
  return (
    <div style={{ padding: '0 0 16px' }}>
      {/* v0.45.28 — Brett (screenshot, red marker through both the
          AppShell tagline banner AND this PlanScreen h1): "i think
          we are wasting space at the top of the screen. Lets make
          some room." The whole header row is gone — the Done
          button moves onto the picker row below so the canvas
          starts immediately. ~50px reclaimed on top of the ~55px
          from killing the AppShell banner. */}

      {/* Picker row */}
      <div style={{ marginBottom: 6, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', color: theme.textLight, letterSpacing: 0.4 }}>Plan</span>
        {loadingDrawings ? (
          <Spinner label="Loading drawings…" />
        ) : drawings.length === 0 ? (
          <span style={{ fontSize: 12, color: theme.textLight }}>No drawings yet — drop a PDF or image onto the canvas below.</span>
        ) : (
          <select
            value={pickedDrawingId || ''}
            onChange={(e) => setPickedDrawingId(parseInt(e.target.value, 10) || null)}
            style={{ flex: '1 1 200px', maxWidth: 360, padding: '6px 8px', border: `1px solid ${theme.border}`, borderRadius: 6, fontSize: 13, fontFamily: 'inherit' }}
          >
            {drawings.map(d => (
              <option key={d.Id} value={d.Id}>
                {d.DrawingNumber || '—'} · {d.Title || '(untitled)'}{d.Revision ? ` · Rev ${d.Revision}` : ''}
              </option>
            ))}
          </select>
        )}
        {/* v0.20.1 — Brett: "i can flip through the drawings continuosly
            and the drawings load slowly so its confusing. Flip up a
            loading drawing spinning wheel." Visible spinner + label
            while the chosen drawing's image is downloading + decoding.
            Also: while the image is loading we hide the pin count text
            (which still resolves to the correct count for the chosen
            drawing) because Brett saw "2 pins on this drawing" + a
            blank canvas and reasonably wondered if the count was lying.
            Once imageLoaded flips true and the pins render, the count
            comes back. */}
        {pickedDrawingId && !imageLoaded ? (
          <span style={{ fontSize: 11, color: theme.brand, fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            <span style={{
              display: 'inline-block', width: 12, height: 12,
              border: '2px solid currentColor', borderRightColor: 'transparent',
              borderRadius: '50%', animation: 'inspec-spin 0.7s linear infinite',
            }} />
            · loading drawing…
          </span>
        ) : pinsOnPicked.length > 0 ? (
          <span style={{ fontSize: 11, color: theme.textLight }}>
            · {pinsOnPicked.length} pin{pinsOnPicked.length === 1 ? '' : 's'} on this drawing
            {pendingOnPicked > 0 && (
              <span style={{ color: theme.warn, fontWeight: 700 }} title="Saved on this phone — not synced yet"> · {pendingOnPicked} not synced</span>
            )}
          </span>
        ) : (
          <span style={{ fontSize: 11, color: theme.textLight }}>· no pins yet — tap the drawing to drop one</span>
        )}
        {uploading && (
          <span style={{ fontSize: 11, color: theme.brand, fontWeight: 600 }}>· uploading…</span>
        )}
        {/* v0.20.1 — keyframes for the loading spinner. Inlined here
            so we don't need to touch the shared stylesheet for a
            single use. */}
        <style>{`@keyframes inspec-spin { to { transform: rotate(360deg); } }`}</style>

        {/* v0.61.0 — Import from InSite. Opens the picker of plans saved
            on this Location in InSite; selecting one imports it as a
            pinnable drawing. marginLeft:auto pushes it (and the Done
            button after it) to the right of the picker row. */}
        <button
          type="button"
          onClick={() => setImportFromInSiteOpen(true)}
          style={{ ...btnSecondary, fontSize: 13, padding: '6px 12px', marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap' }}
          title="Import a plan already saved on this location in InSite"
        >
          <Download size={15} /> Import from InSite
        </button>
        {/* v0.45.28 — Done button moved from the deleted header row
            onto the picker row (right side). Routes back to the
            register/project depending on report scope. */}
        <button
          onClick={() => navigate(`/projects/${projectId}${reportId ? `?reportId=${encodeURIComponent(reportId)}` : ''}`)}
          style={{ ...btnPrimary, fontSize: 13, padding: '6px 14px', marginLeft: 'auto' }}
          title={reportId ? 'Done — back to the report' : 'Done — back to project'}
        >Done</button>
      </div>

      {/* v0.9.1 — Trade legend strip. Click a chip to arm that trade for
          the next pin drop; click again to clear. armedTrade persists
          across multiple drops so a user can place a run of same-trade
          pins without re-arming each time. */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap',
        padding: '6px 8px', marginBottom: 6,
        background: theme.bg, border: `1px solid ${theme.border}`, borderRadius: 6,
      }}>
        {/* v0.20.1 — Brett: "call it pins. Someone may have another
            purpose other then trades." Label broadened from "Trades"
            to "Pins"; the colour chips below still drive the per-pin
            trade tag, but the strip is named for what it actually
            does (control the next pin drop) rather than its data
            model. */}
        <span style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', color: theme.textLight, letterSpacing: 0.4, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
          <MapPin size={12} /> Pins
        </span>
        {TRADES.map(tr => {
          const active = armedTrade === tr.key;
          return (
            <button
              key={tr.key}
              type="button"
              onClick={() => setArmedTrade(active ? null : tr.key)}
              title={active
                ? `${tr.label} is armed — next pin will be tagged ${tr.label}. Click again to clear.`
                : `Arm "${tr.label}" for the next pin drop`}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                padding: '4px 8px', borderRadius: 999,
                background: active ? tr.color + '22' : theme.surface,
                border: active ? `2px solid ${tr.color}` : `1px solid ${theme.border}`,
                color: theme.text,
                fontSize: 12, fontWeight: active ? 700 : 500,
                cursor: 'pointer', fontFamily: 'inherit',
                lineHeight: 1.2,
              }}
            >
              <span style={{
                width: 14, height: 14, borderRadius: '50%',
                background: tr.color, display: 'inline-block',
                boxShadow: '0 0 0 1px rgba(0,0,0,0.08)',
              }} />
              {tr.label}
            </button>
          );
        })}
      </div>

      {/* v0.9.1 — armed-trade hint banner. Faint so it doesn't shout, but
          clear enough the user knows what their next tap will do. */}
      {armedTrade && (
        <div style={{
          marginBottom: 6, padding: '6px 10px', borderRadius: 6,
          background: tradeColor(armedTrade) + '14',
          border: `1px dashed ${tradeColor(armedTrade)}`,
          color: theme.text, fontSize: 12,
          display: 'flex', alignItems: 'center', gap: 8,
        }}>
          <span style={{
            width: 10, height: 10, borderRadius: '50%',
            background: tradeColor(armedTrade), display: 'inline-block',
          }} />
          <span>
            Next pin will be tagged as: <strong>{tradeLabel(armedTrade)}</strong>
            <span style={{ color: theme.textLight }}> — click the chip again to clear.</span>
          </span>
        </div>
      )}

      {/* v0.45.5 — Multi-pin armed banner removed (RFI workflow → InSpec). */}

      {/* Canvas */}
      <div
        ref={stageRef}
        onWheel={onWheel}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        onDragOver={onStageDragOver}
        onDragEnter={onStageDragOver}
        onDragLeave={onStageDragLeave}
        onDrop={onStageDrop}
        style={{
          position: 'relative',
          width: '100%',
          // v0.140.0 — the canvas takes whatever is left of the window. It used
          // to be aspect-fitted to the DRAWING and capped at 78vh, which on a
          // landscape plan left a band of dead page underneath and the plan in a
          // small box (Brett: "any white space should be plan"). Measured from
          // the stage's own top edge, so it stays right whatever the toolbars
          // above it do. 320px floor for a phone in landscape.
          height: stageH || '70vh',
          background: theme.bg,
          borderRadius: 8,
          overflow: 'hidden',
          border: `1px solid ${theme.border}`,
          touchAction: 'none',
          userSelect: 'none',
          cursor: 'crosshair',
        }}
      >
        {error && (
          <div style={{ position: 'absolute', inset: 0, padding: 12, color: theme.danger, fontSize: 12 }}>{error}</div>
        )}
        {loadingFile && (
          <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Spinner label="Loading drawing… (PDFs auto-convert on first open)" />
          </div>
        )}
        {!drawingFileUrl && !loadingFile && !error && (
          <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 6, color: theme.textLight, textAlign: 'center', padding: 20, pointerEvents: 'none' }}>
            <Download size={36} color={theme.textLight} strokeWidth={1.5} />
            <div style={{ fontSize: 14, fontWeight: 600, color: theme.text }}>Drop a PDF or image here</div>
            <div style={{ fontSize: 12, color: theme.textLight }}>…or pick a drawing from the dropdown above. PDFs auto-convert to PNG.</div>
          </div>
        )}
        {dropping && (
          <div style={{ position: 'absolute', inset: 0, background: 'rgba(251,146,60,0.18)', border: `3px dashed ${theme.brand}`, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', color: theme.brandDark, fontWeight: 700, fontSize: 16, pointerEvents: 'none', zIndex: 10 }}>
            Drop to attach
          </div>
        )}
        {/* v0.33.0 — Page-turn prev/next overlay arrows. Brett: the
            screenshot has red arrows on either side of the drawing.
            Floating chevrons centered vertically, semi-transparent,
            brand-orange on hover. Only render when there's more than
            one drawing — otherwise they're dead weight. */}
        {drawings.length > 1 && pickedDrawingId != null && (() => {
          const idx = drawings.findIndex(d => d.Id === pickedDrawingId);
          const prev = idx > 0 ? drawings[idx - 1] : drawings[drawings.length - 1];
          const next = idx < drawings.length - 1 ? drawings[idx + 1] : drawings[0];
          const goto = (d) => setPickedDrawingId(d.Id);
          return (<>
            <PagerArrow side="left"  label="Previous drawing"
              subtitle={`${idx === 0 ? drawings.length : idx} of ${drawings.length}`}
              target={prev} onClick={() => goto(prev)} />
            <PagerArrow side="right" label="Next drawing"
              subtitle={`${idx + 2 > drawings.length ? 1 : idx + 2} of ${drawings.length}`}
              target={next} onClick={() => goto(next)} />
          </>);
        })()}
        {uploading && (
          <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 10, color: 'white', fontSize: 14, zIndex: 11 }}>
            <span style={{
              width: 38, height: 38, borderRadius: '50%',
              border: '4px solid rgba(255,255,255,0.25)', borderTopColor: theme.brand,
              animation: 'fdpSpin 0.8s linear infinite', display: 'inline-block',
            }} />
            Converting + uploading…
          </div>
        )}
        {drawingFileUrl && (
          drawingFileUrl.startsWith('data:application/pdf') || /\.pdf(\?|#|$)/i.test(drawingFileUrl) ? (
            // v2.34.30/v2.34.32 (InSpec) — <object> with <embed> fallback
            // for iOS Safari that won't inline-render PDFs.
            <div style={{ position: 'absolute', inset: 0 }}>
              <object
                data={drawingFileUrl}
                type="application/pdf"
                style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 0, background: 'white' }}
              >
                <embed src={drawingFileUrl} type="application/pdf" style={{ width: '100%', height: '100%' }} />
                <div style={{
                  position: 'absolute', inset: 0,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 14,
                  color: theme.text, padding: 24, textAlign: 'center',
                }}>
                  <div style={{ fontSize: 14 }}>This drawing is a PDF and your browser can't preview it inline.</div>
                  <a
                    href={drawingFileUrl}
                    target="_blank"
                    rel="noreferrer"
                    onClick={interceptExternalClick}
                    style={{
                      background: theme.brand, color: 'white', padding: '12px 22px',
                      borderRadius: 999, fontSize: 14, fontWeight: 700, textDecoration: 'none',
                    }}
                  >Open drawing</a>
                </div>
              </object>
              <div style={{
                position: 'absolute', left: 0, right: 0, bottom: 0,
                padding: '6px 10px',
                background: 'rgba(0,0,0,0.65)',
                color: 'rgba(255,255,255,0.9)',
                fontSize: 11,
                textAlign: 'center',
                pointerEvents: 'none',
              }}>
                View-only — to pin defects, drop a PNG version of this drawing onto the canvas.
              </div>
            </div>
          ) : (
            <div
              style={{
                position: 'absolute',
                left: 0, top: 0,
                width: naturalW || 1, height: naturalH || 1,
                transformOrigin: '0 0',
                transform: `translate(${tx}px, ${ty}px) scale(${scale})`,
                willChange: 'transform',
              }}
            >
              <img
                ref={imageRef}
                loading="lazy"
                src={drawingFileUrl}
                alt="Drawing"
                onLoad={onImgLoad}
                draggable={false}
                style={{ width: naturalW || 'auto', height: naturalH || 'auto', display: 'block', pointerEvents: 'none' }}
              />
              {/* v2.11.77 (InSpec) — pending pin (orange, dashed) for the
                  defect currently being filled in via the bottom sheet.
                  v2.11.80 — draggable so you can fine-tune without re-tapping. */}
              {pendingPin && Number(pendingPin.drawingId) === Number(pickedDrawingId) && (
                <button
                  type="button"
                  onPointerDown={(ev) => {
                    ev.stopPropagation();
                    ev.preventDefault();
                    const onMove = (mv) => {
                      const rect = stageRef.current?.getBoundingClientRect();
                      if (!rect || !naturalW || !naturalH) return;
                      const sx = mv.clientX - rect.left;
                      const sy = mv.clientY - rect.top;
                      const imgPtX = (sx - tx) / scale;
                      const imgPtY = (sy - ty) / scale;
                      const nx = Math.min(1, Math.max(0, imgPtX / naturalW));
                      const ny = Math.min(1, Math.max(0, imgPtY / naturalH));
                      const next = { drawingId: pickedDrawingId, markX: Number(nx.toFixed(5)), markY: Number(ny.toFixed(5)) };
                      setPendingPin(next);
                    };
                    const onUp = () => {
                      window.removeEventListener('pointermove', onMove);
                      window.removeEventListener('pointerup', onUp);
                      window.removeEventListener('pointercancel', onUp);
                    };
                    window.addEventListener('pointermove', onMove);
                    window.addEventListener('pointerup', onUp);
                    window.addEventListener('pointercancel', onUp);
                  }}
                  title="Drag to reposition, or tap a new spot on the plan."
                  style={{
                    position: 'absolute',
                    left: Number(pendingPin.markX) * naturalW,
                    top:  Number(pendingPin.markY) * naturalH,
                    // v0.45.11 — Pending pin matches saved pin at 40% scale.
                    transform: `translate(-5px, -22px) scale(${1/(scale||1)})`,
                    transformOrigin: '5px 22px',
                    width: 16, height: 24,
                    background: 'transparent',
                    border: 'none',
                    cursor: 'grab',
                    padding: 0,
                    filter: 'drop-shadow(0 0 3px rgba(251,146,60,0.85))',
                  }}
                  aria-label="Pending pin — drag or tap a new spot to move"
                >
                  <PinHalo />
                  <svg width="16" height="24" viewBox="0 0 40 60" style={{ overflow: 'visible', display: 'block' }} aria-hidden>
                    <ellipse cx="12" cy="56" rx="7" ry="1.8" fill="rgba(0,0,0,0.35)" />
                    <line x1="12" y1="55" x2="22" y2="24" stroke="#5b6470" strokeWidth="2.6" strokeLinecap="round" />
                    <line x1="12.5" y1="54" x2="21.5" y2="24" stroke="rgba(255,255,255,0.35)" strokeWidth="0.8" strokeLinecap="round" />
                    <circle cx="22" cy="18" r="12" fill={theme.brand} stroke="#fff" strokeWidth="2" strokeDasharray="3 2" />
                    <ellipse cx="18" cy="13" rx="4.5" ry="2.8" fill="rgba(255,255,255,0.55)" />
                  </svg>
                </button>
              )}
              {/* Saved pins. v0.9.1 — gated on imageLoaded so we don't
                  flash the previous drawing's pins over the new image
                  while it loads. Also guards on pinsOnPicked belonging
                  to the *currently picked* drawing (pinsOnPicked already
                  filters by Number(d.drawingId) === Number(pickedDrawingId)). */}
              {imageLoaded && pinsOnPicked.map(d => {
                const focused = String(d.id) === String(focusedDefectId)
                              || String(d.id) === String(editingDefect?.id);
                let useX = Number(d.markX), useY = Number(d.markY);
                if (pinDragLive && String(pinDragLive.id) === String(d.id)) {
                  useX = pinDragLive.normX; useY = pinDragLive.normY;
                } else if (pinMove && String(pinMove.id) === String(d.id)) {
                  useX = pinMove.newX; useY = pinMove.newY;
                }
                const cx = useX * naturalW;
                const cy = useY * naturalH;
                const inv = 1 / (scale || 1);
                const isMoving = (pinDragLive && String(pinDragLive.id) === String(d.id))
                              || (pinMove && String(pinMove.id) === String(d.id));
                const tColor = tradeColor(d.trade);
                // v0.45.5 — 3D pushpin SVG replacing the legacy teardrop.
                // Brett's reference image: glossy sphere head, thin
                // angled gray stem, soft shadow on the blueprint.
                // SVG viewBox is 40 wide × 60 tall; the stem tip is at
                // (12, 56) — that's the point that lands on the user's
                // tap coordinates. Translate the button so (12, 56)
                // sits exactly at (cx, cy), then counter-scale for the
                // canvas zoom level. Multi-pin "+N" badge gone (RFI
                // workflow → InSpec).
                // v0.45.11 — Brett: "The pin on the plans are still too
                // big." Halved again from v0.45.8's 24×36 down to
                // 16×24 (40% of original 40×60). Tip anchor: viewBox
                // tip at (12, 56) → screen tip at (12*16/40, 56*24/60)
                // = (4.8, 22.4). Round to 5 and 22.
                return (
                  <button
                    key={d.id}
                    data-pin-id={String(d.id)}
                    onPointerDown={(ev) => beginPinDrag(ev, d)}
                    title={(d.pending ? '[Not synced — saved on this phone] ' : '') + (d.description || '')}
                    style={{
                      position: 'absolute',
                      left: cx, top: cy,
                      // 2026-09-15 — the selected pin is drawn larger and above
                      // its neighbours, and wears the pulsing rings (PinHalo).
                      transform: `translate(-5px, -22px) scale(${focused ? inv * 1.35 : inv})`,
                      transformOrigin: '5px 22px',
                      width: 16, height: 24,
                      background: 'transparent',
                      border: 'none',
                      padding: 0,
                      cursor: 'pointer',
                      zIndex: focused ? 3 : undefined,
                      pointerEvents: 'auto',
                      filter: isMoving
                        ? 'drop-shadow(0 0 3px rgba(59,130,246,0.85))'
                        : (focused ? 'drop-shadow(0 0 3px rgba(251,146,60,0.85))' : 'none'),
                    }}
                    aria-label={`Defect: ${d.description}`}
                  >
                    {focused && <PinHalo />}
                    <svg
                      width="16" height="24" viewBox="0 0 40 60"
                      style={{ overflow: 'visible', display: 'block' }}
                      aria-hidden
                    >
                      <ellipse cx="12" cy="56" rx="7" ry="1.8" fill="rgba(0,0,0,0.35)" />
                      <line x1="12" y1="55" x2="22" y2="24" stroke="#5b6470" strokeWidth="2.6" strokeLinecap="round" />
                      <line x1="12.5" y1="54" x2="21.5" y2="24" stroke="rgba(255,255,255,0.35)" strokeWidth="0.8" strokeLinecap="round" />
                      {/* v0.45.26 — Brett: "When i click the pin on the
                          plan it appears to move." Radius held constant
                          at 12; only the stroke COLOUR flips on focus.
                          Stroke width also held constant so the visible
                          head diameter doesn't change. The orange
                          drop-shadow on the parent button already gives
                          a clear focus signal without geometry shifts. */}
                      <circle
                        cx="22" cy="18" r="12"
                        fill={tColor}
                        stroke={focused ? '#fff' : 'rgba(0,0,0,0.25)'}
                        strokeWidth="1"
                      />
                      <ellipse cx="18" cy="13" rx="4.5" ry="2.8" fill="rgba(255,255,255,0.55)" />
                      {/* v0.171.0 — saved on this phone, not yet synced. */}
                      {d.pending && (
                        <circle cx="34" cy="6" r="7" style={{ fill: theme.warn }} stroke="#fff" strokeWidth="2" />
                      )}
                    </svg>
                  </button>
                );
              })}
            </div>
          )
        )}

        {/* v0.79 — Plan markup + measure. The overlay renders its own
            transformed wrapper (shares translate/scale with the image) so it
            registers pixel-exact; the toolbar + entry button are screen-fixed
            in the canvas. Raster drawings only (same as pins — PDFs stay
            view-only). */}
        {drawingFileUrl && !loadingFile && !error &&
          !(drawingFileUrl.startsWith('data:application/pdf') || /\.pdf(\?|#|$)/i.test(drawingFileUrl)) && (
          markupEnabled ? (
            <>
              <MarkupLayer
                m={markup}
                naturalW={naturalW}
                naturalH={naturalH}
                scale={scale}
                tx={tx}
                ty={ty}
                clientToImagePx={clientToImagePx}
                onPanStart={beginPan}
                markTypes={markTypes}
              />
              <MarkupToolbar m={markup} drawingId={pickedDrawingId} markTypes={markTypes} naturalW={naturalW} naturalH={naturalH} onExit={() => setMarkupEnabled(false)} />
              {markup.showTotals && (
                <MarkupTotalsPanel
                  m={markup}
                  naturalW={naturalW}
                  naturalH={naturalH}
                  markTypes={markTypes}
                  onClose={() => markup.setShowTotals(false)}
                />
              )}
              <CalibrationDialog
                open={!!markup.calibDraft}
                onConfirm={markup.confirmCalibration}
                onCancel={markup.cancelCalibration}
                refA={markup.calibDraft && markup.calibDraft.refA}
                refB={markup.calibDraft && markup.calibDraft.refB}
                naturalW={naturalW}
                naturalH={naturalH}
              />
            </>
          ) : (
            <>
              {/* v0.139.0 — the marks STAY ON THE PLAN when markup is closed.
                  They used to vanish with the overlay, so an area you'd just
                  measured looked like it had never been drawn (Brett: "it looks
                  like the areas arent coming through to the plan after i get out
                  of markup"). Read-only: no input, no handles, pins still work. */}
              {markup.count > 0 && (
                <MarkupLayer
                  readOnly
                  m={markup}
                  naturalW={naturalW}
                  naturalH={naturalH}
                  scale={scale}
                  tx={tx}
                  ty={ty}
                  clientToImagePx={clientToImagePx}
                  markTypes={markTypes}
                />
              )}
            <button
              type="button"
              onClick={() => setMarkupEnabled(true)}
              title="Markup & measure — annotate and measure this plan"
              style={{
                position: 'absolute', top: 8, left: 8, zIndex: 12,
                display: 'inline-flex', alignItems: 'center', gap: 6,
                padding: '7px 12px', borderRadius: 8, border: 'none',
                background: 'rgba(15,23,42,0.78)', color: '#fff',
                fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit',
              }}
            >
              <PencilRuler size={15} strokeWidth={2} /> Markup
            </button>
            </>
          )
        )}

        {/* Floating zoom controls */}
        {drawingFileUrl && !loadingFile && !error && (
          <div style={{
            position: 'absolute', top: 8, right: 8, display: 'flex', flexDirection: 'column', gap: 6,
            background: 'rgba(0,0,0,0.55)', padding: 6, borderRadius: 8,
          }}>
            <button onClick={zoomIn}  style={zoomBtnStyle} title="Zoom in (mouse wheel up)">＋</button>
            <button onClick={zoomOut} style={zoomBtnStyle} title="Zoom out (mouse wheel down)">−</button>
            <button onClick={fit}     style={{ ...zoomBtnStyle, fontSize: 12 }} title="Fit to view (double-tap)">FIT</button>
            <div style={{ fontSize: 10, fontWeight: 700, color: 'rgba(255,255,255,0.85)', textAlign: 'center', marginTop: 2 }}>
              {Math.round(scale * 100)}%
            </div>
          </div>
        )}
      </div>

      <style>{`
        ${PIN_HALO_CSS}
        @keyframes fdpSpin {
          from { transform: rotate(0deg); }
          to   { transform: rotate(360deg); }
        }
      `}</style>

      {/* v2.18.18 (InSpec) — Sticky Save bar after a saved pin has been dragged. */}
      {pinMove && (
        <div style={{
          position: 'fixed', left: 0, right: 0, bottom: 0, zIndex: 1600,
          background: '#0e0f10', color: 'white',
          padding: '12px 16px',
          display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
          boxShadow: '0 -6px 24px rgba(0,0,0,0.45)',
          borderTop: '1px solid rgba(255,255,255,0.1)',
        }}>
          <div style={{ flex: '1 1 240px', minWidth: 0 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#93c5fd', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Pin moved
            </div>
            <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.85)', marginTop: 2 }}>
              Save the new position?
            </div>
          </div>
          <button onClick={cancelPinMove} disabled={pinSaving}
            style={{
              padding: '10px 16px', borderRadius: 8,
              background: 'transparent', color: 'white',
              border: '1px solid rgba(255,255,255,0.3)',
              fontSize: 13, fontWeight: 700, cursor: pinSaving ? 'default' : 'pointer',
              fontFamily: 'inherit', opacity: pinSaving ? 0.6 : 1,
            }}>Cancel</button>
          <button onClick={savePinMove} disabled={pinSaving}
            style={{
              padding: '10px 18px', borderRadius: 8,
              background: theme.brand, color: 'white', border: 0,
              fontSize: 13, fontWeight: 800, cursor: pinSaving ? 'default' : 'pointer',
              fontFamily: 'inherit', opacity: pinSaving ? 0.7 : 1,
            }}>{pinSaving ? 'Saving…' : 'Save new position'}</button>
        </div>
      )}

      {/* The pin card — defect description / priority / status / assignee / photos.
          v0.4.0 (DropPin port) — In InSpec this was in FieldDefects.jsx.
          2026-10-29 (v0.263.0) — its own component, shared with the register. */}
      {sheetOpen && (
        <DefectEditorSheet
          key={sheetSeq}
          defect={editingDefect}
          onPinAt={pinAtPoint}
          onSwitchPin={focusAndOpenDefect}
          pendingPin={pendingPin}
          armedTrade={armedTrade}
          projectContacts={projectContacts}
          drawings={drawings}
          onSave={onSheetSave}
          onDelete={editingDefect ? onSheetDelete : null}
          onCancel={onSheetCancel}
          // v0.287.0 — Previous / Next walk this drawing's pins in the arrow
          // keys' order, zooming to each like a tap on it.
          items={editingDefect ? pinsOnPicked : null}
          onOpenItem={focusAndOpenDefect}
        />
      )}

      {/* v0.38.1 — Free-tier upgrade nudge for defect-cap hits. */}
      <UpgradePrompt
        open={!!upgradeReason}
        reason={upgradeReason}
        onClose={() => setUpgradeReason(null)}
      />

      {/* v0.45.25 — Multi-file upload rename modal. Pops when files
          are queued via window.__droppin_pending_plans__ (handed off
          from RegisterScreen's PlansManagerModal). Same component
          ProjectDetailScreen uses for its drop tile, so the upload
          UX is identical between the two entry points. */}
      {planQueue.length > 0 && (
        <PlanRenameModal
          file={planQueue[0].file}
          defaultName={planQueue[0].defaultName}
          uploading={planUploading}
          queueIndex={0}
          queueTotal={planQueue.length}
          onCancel={cancelPlanQueue}
          onSkip={planQueue.length > 1 ? skipCurrentPlan : null}
          onConfirm={(nickname) => uploadCurrentPlanQueued(nickname)}
        />
      )}

      {/* v0.61.0 — Import-from-InSite picker. Lists plans saved on this
          Location in InSite; selecting one POSTs /api/import-location-plan
          and we refresh the drawing list + select the new plan so it's
          immediately pinnable. */}
      {importFromInSiteOpen && (
        <ImportFromInSiteModal
          projectId={projectId}
          reportId={reportId}
          onImported={async (newId) => {
            try {
              const ld = await apiGet(reportId
                ? `/api/drawings?reportId=${encodeURIComponent(reportId)}`
                : `/api/drawings?projectId=${encodeURIComponent(projectId)}`);
              const arr = Array.isArray(ld) ? ld.map(drawingFromApi) : [];
              setDrawings(arr);
            } catch (_) { /* keep current list */ }
            if (newId) { setDrawingFileUrl(null); setPickedDrawingId(Number(newId)); }
          }}
          onClose={() => setImportFromInSiteOpen(false)}
        />
      )}
    </div>
  );
}

const zoomBtnStyle = {
  width: 36, height: 36,
  background: 'white',
  color: theme.text,
  border: 'none',
  borderRadius: 6,
  fontSize: 18,
  fontWeight: 700,
  cursor: 'pointer',
  fontFamily: 'inherit',
};

// v0.33.0 — Pin glyph used as the "I" in the "Drop a Pin" title.
// Brett: "Can the I in pin be an actual pin." Matches the brand-orange
// gradient pin from the home blueprint hero. Sized to align with the
// surrounding caps.
function PinAsI({ height = 20 }) {
  const id = React.useId ? React.useId() : Math.random().toString(36).slice(2, 8);
  return (
    <svg
      viewBox="0 0 14 22"
      height={height}
      width={height * (14 / 22)}
      aria-hidden="true"
      style={{ display: 'inline-block', verticalAlign: '-2px', marginTop: -1 }}
    >
      <defs>
        <radialGradient id={`pin-i-head-${id}`} cx="35%" cy="30%" r="70%">
          <stop offset="0"   stopColor="#ff9a4d" />
          <stop offset="0.6" stopColor="#e8650a" />
          <stop offset="1"   stopColor="#7c2d12" />
        </radialGradient>
        <linearGradient id={`pin-i-shaft-${id}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0"   stopColor="#d4d4d8" />
          <stop offset="0.5" stopColor="#9ca3af" />
          <stop offset="1"   stopColor="#374151" />
        </linearGradient>
      </defs>
      {/* shaft */}
      <path d="M 6 9 L 7 20.5 L 8 9 Z" fill={`url(#pin-i-shaft-${id})`}
            stroke="#1f2937" strokeWidth="0.25" />
      {/* head — sized to roughly the cap-height of a 20px letter */}
      <circle cx="7" cy="6.5" r="5" fill={`url(#pin-i-head-${id})`}
              stroke="#7c2d12" strokeWidth="0.4" />
      {/* specular highlight */}
      <ellipse cx="5.5" cy="4.5" rx="1.4" ry="1.1" fill="#fff" opacity="0.55" />
    </svg>
  );
}

// v0.33.0 — Page-turn-style prev/next overlay arrow on the drawing
// canvas. Brett drew red arrows on either side of the drawing in the
// 2026-05-27 screenshot. Floating, vertically centered, semi-transparent,
// brand-orange chevron with the target drawing's short label underneath.
function PagerArrow({ side, label, subtitle, target, onClick }) {
  const [hover, setHover] = React.useState(false);
  const isLeft = side === 'left';
  const targetTitle = target
    ? `${target.DrawingNumber || ''}${target.DrawingNumber ? ' · ' : ''}${target.Title || '(untitled)'}`.slice(0, 80)
    : '';
  return (
    <button
      onClick={(e) => { e.stopPropagation(); onClick && onClick(); }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      title={`${label}: ${targetTitle}`}
      style={{
        position: 'absolute',
        top: '50%',
        [isLeft ? 'left' : 'right']: 10,
        transform: 'translateY(-50%)',
        background: hover ? 'rgba(232,101,10,0.95)' : theme.surface,
        color: hover ? 'white' : theme.text,
        border: `1px solid ${hover ? theme.brand : theme.border}`,
        borderRadius: 999,
        width: 44, height: 44,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        cursor: 'pointer', fontFamily: 'inherit',
        boxShadow: '0 4px 12px rgba(0,0,0,0.18)',
        zIndex: 15,
        transition: 'background-color 0.12s, color 0.12s, transform 0.12s',
      }}
      aria-label={`${label} — ${targetTitle}`}
    >
      <span style={{ fontSize: 22, lineHeight: 1, fontWeight: 700 }}>
        {isLeft ? '‹' : '›'}
      </span>
      {/* tiny "1 of N" caption underneath the chevron */}
      <span style={{
        position: 'absolute',
        top: '110%',
        whiteSpace: 'nowrap',
        fontSize: 10, fontWeight: 600,
        color: theme.textLight,
        background: theme.surface,
        padding: '2px 6px', borderRadius: 4,
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
      }}>{subtitle}</span>
    </button>
  );
}
